import { useCallback, useRef, useState } from 'react';
import { useCalculateOrder } from '@/api/orders/use-orders';
import { useCrmCalculateTrade } from '@/api/instruments/use-crm-calculate';

interface CalculationParams {
    instrument_id: string;
    trading_account_id: string;
    side: 'buy' | 'sell';
    lot_size: number;
    leverage: number;
    price?: number;
}

type CalculationVariant = 'trader' | 'crm';

export function useDebouncedCalculation(variant: CalculationVariant = 'trader') {
    const [calculation, setCalculation] = useState<any>(null);
    const traderCalculateMutation = useCalculateOrder();
    const crmCalculateMutation = useCrmCalculateTrade();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const requestIdRef = useRef(0);

    const calculateOrder = useCallback(
        (params: CalculationParams) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                const requestId = ++requestIdRef.current;

                const onSuccess = (data: unknown) => {
                    if (requestId !== requestIdRef.current) {
                        return;
                    }
                    setCalculation(data);
                };

                if (variant === 'crm') {
                    crmCalculateMutation.mutate(
                        {
                            instrument_id: params.instrument_id,
                            trading_account_id: params.trading_account_id,
                            lot_size: params.lot_size,
                            leverage: params.leverage,
                            price: params.price,
                            side: params.side,
                        },
                        { onSuccess, onError: () => {} }
                    );
                    return;
                }

                traderCalculateMutation.mutate(params, {
                    onSuccess,
                    onError: () => {},
                });
            }, 1000);
        },
        [variant, traderCalculateMutation, crmCalculateMutation]
    );

    const clearCalculation = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        requestIdRef.current += 1;
        setCalculation(null);
    }, []);

    const isCalculating =
        variant === 'crm' ? crmCalculateMutation.isPending : traderCalculateMutation.isPending;

    return {
        calculation,
        calculateOrder,
        clearCalculation,
        isCalculating,
    };
}
